import {
  collection,
  doc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  updateDoc,
  writeBatch,
  serverTimestamp
} from 'firebase/firestore';
import { ref, listAll, deleteObject } from 'firebase/storage';
import { db, storage } from './firebase';

/**
 * Comprehensive client cascade deletion utility
 * Handles cleanup of all related data when a client is deleted
 */
export class ClientCascadeDeleteService {
  /**
   * Delete a client and all related data
   */
  async cascadeDeleteClient(clientId: string, userId: string): Promise<{
    success: boolean;
    deletedItems: {
      client: boolean;
      insights: number;
      content: number;
      collaborativeTasks: number;
      conversations: number;
      storageFiles: number;
      ragDocuments?: number;
      agentMemories?: number;
      proposedMemories?: number;
      legacyLearned?: number;
    };
    errors: string[];
  }> {
    const deletedItems: {
      client: boolean;
      insights: number;
      content: number;
      collaborativeTasks: number;
      conversations: number;
      storageFiles: number;
      ragDocuments?: number;
      agentMemories?: number;
      proposedMemories?: number;
      legacyLearned?: number;
    } = {
      client: false,
      insights: 0,
      content: 0,
      collaborativeTasks: 0,
      conversations: 0,
      storageFiles: 0
    };
    const errors: string[] = [];

    try {
      console.log(`Starting cascade delete for client: ${clientId}`);

      // 1. Delete client insights
      try {
        const insightsDeleted = await this.deleteClientInsights(clientId);
        deletedItems.insights = insightsDeleted;
        console.log(`Deleted ${insightsDeleted} client insights`);
      } catch (error) {
        console.error('Error deleting client insights:', error);
        errors.push(`Failed to delete insights: ${error}`);
      }

      // 2. Delete content documents
      try {
        const contentDeleted = await this.deleteContentDocuments(clientId, userId);
        deletedItems.content = contentDeleted;
        console.log(`Deleted ${contentDeleted} content documents`);
      } catch (error) {
        console.error('Error deleting content documents:', error);
        errors.push(`Failed to delete content: ${error}`);
      }

      // 3. Delete collaborative tasks
      try {
        const tasksDeleted = await this.deleteCollaborativeTasks(clientId, userId);
        deletedItems.collaborativeTasks = tasksDeleted;
        console.log(`Deleted ${tasksDeleted} collaborative tasks`);
      } catch (error) {
        console.error('Error deleting collaborative tasks:', error);
        errors.push(`Failed to delete tasks: ${error}`);
      }

      // 4. Update conversations to remove client references
      try {
        const conversationsUpdated = await this.removeClientFromConversations(clientId, userId);
        deletedItems.conversations = conversationsUpdated;
        console.log(`Updated ${conversationsUpdated} conversations`);
      } catch (error) {
        console.error('Error updating conversations:', error);
        errors.push(`Failed to update conversations: ${error}`);
      }

      // 4.5 Delete agent memories (LangGraph vector docs) for this user+client
      try {
        const { memoriesDeleted, proposedDeleted, legacyDeleted } = await this.deleteAgentMemoriesAndLearned(userId, clientId);
        deletedItems.agentMemories = memoriesDeleted;
        deletedItems.proposedMemories = proposedDeleted;
        deletedItems.legacyLearned = legacyDeleted;
        console.log(`Deleted ${memoriesDeleted} agent memories, ${proposedDeleted} proposed memories, ${legacyDeleted} legacy learned interactions`);
      } catch (error) {
        console.error('Error deleting agent memories:', error);
        errors.push(`Failed to delete agent memories: ${error}`);
      }

      // 5. Delete storage files
      try {
        const storageFilesDeleted = await this.deleteStorageFiles(clientId);
        deletedItems.storageFiles = storageFilesDeleted;
        console.log(`Deleted ${storageFilesDeleted} storage files`);
      } catch (error) {
        console.error('Error deleting storage files:', error);
        errors.push(`Failed to delete storage files: ${error}`);
      }

      // 5.5 Delete RAG documents from Supabase
      try {
        const ragDocsDeleted = await this.deleteRagDocuments(clientId);
        deletedItems.ragDocuments = ragDocsDeleted;
        console.log(`Deleted ${ragDocsDeleted} RAG documents`);
      } catch (error) {
        console.error('Error deleting RAG documents:', error);
        errors.push(`Failed to delete RAG documents: ${error}`);
      }

      // 6. Finally, delete the client document itself
      try {
        await deleteDoc(doc(db, 'clients', clientId));
        deletedItems.client = true;
        console.log(`Deleted client document: ${clientId}`);
      } catch (error) {
        console.error('Error deleting client document:', error);
        errors.push(`Failed to delete client: ${error}`);
      }

      const success = deletedItems.client && errors.length === 0;
      
      console.log('Cascade delete completed:', { success, deletedItems, errors });
      
      return {
        success,
        deletedItems,
        errors
      };

    } catch (error) {
      console.error('Cascade delete failed:', error);
      errors.push(`Cascade delete failed: ${error}`);
      
      return {
        success: false,
        deletedItems,
        errors
      };
    }
  }

  /**
   * Delete all client insights for a specific client
   */
  private async deleteClientInsights(clientId: string): Promise<number> {
    const insightsQuery = query(
      collection(db, 'clientInsights'),
      where('clientId', '==', clientId)
    );

    const querySnapshot = await getDocs(insightsQuery);
    
    if (querySnapshot.empty) {
      return 0;
    }

    // Use batch delete for efficiency
    const batch = writeBatch(db);
    querySnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });
    
    await batch.commit();
    return querySnapshot.size;
  }

  /**
   * Delete all content documents for a specific client
   */
  private async deleteContentDocuments(clientId: string, userId: string): Promise<number> {
    const contentQuery = query(
      collection(db, 'contentDocuments'),
      where('clientId', '==', clientId),
      where('userId', '==', userId)
    );

    const querySnapshot = await getDocs(contentQuery);
    
    if (querySnapshot.empty) {
      return 0;
    }

    // Use batch delete for efficiency
    const batch = writeBatch(db);
    querySnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });
    
    await batch.commit();
    return querySnapshot.size;
  }

  /**
   * Delete all collaborative tasks for a specific client
   */
  private async deleteCollaborativeTasks(clientId: string, userId: string): Promise<number> {
    const tasksQuery = query(
      collection(db, 'collaborativeTasks'),
      where('clientId', '==', clientId),
      where('userId', '==', userId)
    );

    const querySnapshot = await getDocs(tasksQuery);
    
    if (querySnapshot.empty) {
      return 0;
    }

    // Use batch delete for efficiency
    const batch = writeBatch(db);
    querySnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });
    
    await batch.commit();
    return querySnapshot.size;
  }

  /**
   * Remove client references from conversations (don't delete conversations, just remove clientId)
   */
  private async removeClientFromConversations(clientId: string, userId: string): Promise<number> {
    const conversationsQuery = query(
      collection(db, `users/${userId}/conversations`),
      where('clientId', '==', clientId)
    );

    const querySnapshot = await getDocs(conversationsQuery);
    
    if (querySnapshot.empty) {
      return 0;
    }

    // Use batch update to remove clientId references
    const batch = writeBatch(db);
    querySnapshot.docs.forEach(doc => {
      batch.update(doc.ref, {
        clientId: null,
        updatedAt: serverTimestamp()
      });
    });
    
    await batch.commit();
    return querySnapshot.size;
  }

  /**
   * Delete all storage files for a specific client
   */
  private async deleteStorageFiles(clientId: string): Promise<number> {
    let deletedCount = 0;

    try {
      // List all files in the client's storage folder
      const clientStorageRef = ref(storage, `clients/${clientId}`);
      const listResult = await listAll(clientStorageRef);
      
      // Delete all files
      const deletePromises = listResult.items.map(async (item) => {
        await deleteObject(item);
        deletedCount++;
      });

      // Delete all subdirectories
      const subdirectoryPromises = listResult.prefixes.map(async (prefix) => {
        const subListResult = await listAll(prefix);
        const subDeletePromises = subListResult.items.map(async (item) => {
          await deleteObject(item);
          deletedCount++;
        });
        await Promise.all(subDeletePromises);
      });

      await Promise.all([...deletePromises, ...subdirectoryPromises]);
      
    } catch (error) {
      // If the folder doesn't exist, that's fine - nothing to delete
      if (error && typeof error === 'object' && 'code' in error && error.code === 'storage/object-not-found') {
        console.log(`No storage files found for client ${clientId}`);
        return 0;
      }
      throw error;
    }

    return deletedCount;
  }

  /**
   * Delete all RAG documents for a specific client from Supabase
   * Fetches client's relevantDocuments from Firestore, then calls /api/rag/delete for each
   */
  private async deleteRagDocuments(clientId: string): Promise<number> {
    let deletedCount = 0;

    try {
      // Fetch the client document to get the list of RAG document IDs
      const clientRef = doc(db, 'clients', clientId);
      const clientSnap = await getDoc(clientRef);

      if (!clientSnap.exists()) {
        console.log(`Client ${clientId} not found, skipping RAG cleanup`);
        return 0;
      }

      const clientData = clientSnap.data();
      const relevantDocuments = clientData?.context?.relevantDocuments || [];

      if (relevantDocuments.length === 0) {
        console.log(`No RAG documents to delete for client ${clientId}`);
        return 0;
      }

      // Extract ragDocId or id from each document
      const docIds: string[] = relevantDocuments
        .map((docItem: any) => docItem.ragDocId || docItem.id)
        .filter(Boolean);

      if (docIds.length === 0) {
        console.log(`No valid RAG document IDs found for client ${clientId}`);
        return 0;
      }

      // Delete each RAG document via the API proxy
      const deletePromises = docIds.map(async (docId: string) => {
        try {
          const resp = await fetch('/api/rag/delete', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ docId, clientId }),
          });

          if (resp.ok) {
            deletedCount++;
            console.log(`[rag-cascade] Deleted RAG doc: ${docId}`);
          } else {
            const errData = await resp.json().catch(() => ({}));
            console.warn(`[rag-cascade] Failed to delete ${docId}:`, errData?.error || resp.status);
          }
        } catch (err: any) {
          console.warn(`[rag-cascade] Error deleting ${docId}:`, err?.message || err);
        }
      });

      await Promise.all(deletePromises);

    } catch (error) {
      console.error('Error in deleteRagDocuments:', error);
      // Don't throw - we want cascade to continue even if RAG cleanup fails
    }

    return deletedCount;
  }

  /**
   * Delete agent memories and learned interactions for a specific client and user.
   * - aiModels/{userId}/memories where clientId == clientId OR tags array-contains clientId
   * - aiModels/{userId}/proposed_memories where clientId == clientId
   * - learnedInteractions (top-level) where userId == userId AND clientId == clientId
   */
  private async deleteAgentMemoriesAndLearned(userId: string, clientId: string): Promise<{ memoriesDeleted: number; proposedDeleted: number; legacyDeleted: number; }>
  {
    let memoriesDeleted = 0;
    let proposedDeleted = 0;
    let legacyDeleted = 0;

    // Helper to commit batched deletes in chunks
    const commitInChunks = async (refs: any[]) => {
      const CHUNK = 400; // be safe below 500 limit
      for (let i = 0; i < refs.length; i += CHUNK) {
        const batch = writeBatch(db);
        for (const r of refs.slice(i, i + CHUNK)) batch.delete(r);
        await batch.commit();
      }
    };

    // 1) Delete aiModels/{userId}/memories
    {
      const base = collection(db, 'aiModels', userId, 'memories');
      const byClient = await getDocs(query(base, where('clientId', '==', clientId)));
      const byTag = await getDocs(query(base, where('tags', 'array-contains', clientId)));
      const map = new Map<string, any>();
      byClient.docs.forEach(d => map.set(d.id, d.ref));
      byTag.docs.forEach(d => map.set(d.id, d.ref));
      const refs = Array.from(map.values());
      if (refs.length) {
        await commitInChunks(refs);
        memoriesDeleted += refs.length;
      }
    }

    // 2) Delete aiModels/{userId}/proposed_memories
    {
      const base = collection(db, 'aiModels', userId, 'proposed_memories');
      const snap = await getDocs(query(base, where('clientId', '==', clientId)));
      if (!snap.empty) {
        const refs = snap.docs.map(d => d.ref);
        await commitInChunks(refs);
        proposedDeleted += refs.length;
      }
    }

    // 3) Delete legacy top-level learnedInteractions
    {
      const base = collection(db, 'learnedInteractions');
      const snap = await getDocs(query(base, where('clientId', '==', clientId), where('userId', '==', userId)));
      if (!snap.empty) {
        const refs = snap.docs.map(d => d.ref);
        await commitInChunks(refs);
        legacyDeleted += refs.length;
      }
    }

    return { memoriesDeleted, proposedDeleted, legacyDeleted };
  }

  /**
   * Get a preview of what would be deleted (for confirmation dialogs)
   */
  async getDeletePreview(clientId: string, userId: string): Promise<{
    insights: number;
    content: number;
    collaborativeTasks: number;
    conversations: number;
    storageFiles: number;
    ragDocuments: number;
    agentMemories: number;
    proposedMemories: number;
    legacyLearned: number;
    estimatedTotal: number;
  }> {
    const preview: {
      insights: number;
      content: number;
      collaborativeTasks: number;
      conversations: number;
      storageFiles: number;
      ragDocuments: number;
      agentMemories: number;
      proposedMemories: number;
      legacyLearned: number;
      estimatedTotal: number;
    } = {
      insights: 0,
      content: 0,
      collaborativeTasks: 0,
      conversations: 0,
      storageFiles: 0,
      ragDocuments: 0,
      agentMemories: 0,
      proposedMemories: 0,
      legacyLearned: 0,
      estimatedTotal: 0
    };

    try {
      // Count client insights
      const insightsQuery = query(
        collection(db, 'clientInsights'),
        where('clientId', '==', clientId)
      );
      const insightsSnapshot = await getDocs(insightsQuery);
      preview.insights = insightsSnapshot.size;

      // Count content documents
      const contentQuery = query(
        collection(db, 'contentDocuments'),
        where('clientId', '==', clientId),
        where('userId', '==', userId)
      );
      const contentSnapshot = await getDocs(contentQuery);
      preview.content = contentSnapshot.size;

      // Count collaborative tasks
      const tasksQuery = query(
        collection(db, 'collaborativeTasks'),
        where('clientId', '==', clientId),
        where('userId', '==', userId)
      );
      const tasksSnapshot = await getDocs(tasksQuery);
      preview.collaborativeTasks = tasksSnapshot.size;

      // Count conversations with client references
      const conversationsQuery = query(
        collection(db, `users/${userId}/conversations`),
        where('clientId', '==', clientId)
      );
      const conversationsSnapshot = await getDocs(conversationsQuery);
      preview.conversations = conversationsSnapshot.size;

      // Count storage files (rough estimate)
      try {
        const clientStorageRef = ref(storage, `clients/${clientId}`);
        const listResult = await listAll(clientStorageRef);
        preview.storageFiles = listResult.items.length;

        // Count files in subdirectories
        for (const prefix of listResult.prefixes) {
          const subListResult = await listAll(prefix);
          preview.storageFiles += subListResult.items.length;
        }
      } catch (error) {
        // Storage folder doesn't exist
        preview.storageFiles = 0;
      }

      // Count RAG documents (from client's relevantDocuments)
      try {
        const clientRef = doc(db, 'clients', clientId);
        const clientSnap = await getDoc(clientRef);
        if (clientSnap.exists()) {
          const clientData = clientSnap.data();
          const relevantDocs = clientData?.context?.relevantDocuments || [];
          const ragDocIds = relevantDocs
            .map((d: any) => d.ragDocId || d.id)
            .filter(Boolean);
          preview.ragDocuments = ragDocIds.length;
        }
      } catch (error) {
        preview.ragDocuments = 0;
      }

      // Count memories under aiModels/{userId}
      try {
        const memBase = collection(db, 'aiModels', userId, 'memories');
        const byClient = await getDocs(query(memBase, where('clientId', '==', clientId)));
        const byTag = await getDocs(query(memBase, where('tags', 'array-contains', clientId)));
        const ids = new Set<string>();
        byClient.docs.forEach(d => ids.add(d.id));
        byTag.docs.forEach(d => ids.add(d.id));
        preview.agentMemories = ids.size;
      } catch {}

      // Count proposed memories
      try {
        const propBase = collection(db, 'aiModels', userId, 'proposed_memories');
        const snap = await getDocs(query(propBase, where('clientId', '==', clientId)));
        preview.proposedMemories = snap.size;
      } catch {}

      // Count legacy learnedInteractions
      try {
        const liBase = collection(db, 'learnedInteractions');
        const snap = await getDocs(query(liBase, where('clientId', '==', clientId), where('userId', '==', userId)));
        preview.legacyLearned = snap.size;
      } catch {}

      preview.estimatedTotal = preview.insights + preview.content + preview.collaborativeTasks + preview.storageFiles + preview.ragDocuments + preview.agentMemories + preview.proposedMemories + preview.legacyLearned;

    } catch (error) {
      console.error('Error getting delete preview:', error);
    }

    return preview;
  }

  /**
   * Verify client ownership before deletion
   */
  async verifyClientOwnership(clientId: string, userId: string): Promise<boolean> {
    try {
      const clientDoc = await getDocs(query(
        collection(db, 'clients'),
        where('__name__', '==', clientId),
        where('createdBy', '==', userId)
      ));
      
      return !clientDoc.empty;
    } catch (error) {
      console.error('Error verifying client ownership:', error);
      return false;
    }
  }
}

// Export singleton instance
export const clientCascadeDelete = new ClientCascadeDeleteService();
